import React, { FC, useState } from "react";
import styled from "styled-components";
import { useRetryWaybackAdminMutation } from "../../api/artOfTheDayApi";

import type {
  AOTDServerData,
  AotdStolenLineageEntryData,
  BlockedAttemptData,
  EarlyAttemptData,
  PipelineStep,
  RaceParticipantEntryData,
  WaybackMetadataData,
} from "./types";
import {
  Card,
  CodeBlock,
  Disclosure,
  ErrorText,
  Hint,
  Mono,
  Pill,
  Row,
  SectionTitle,
  Table,
  TableScroll,
  colors,
} from "./styles";

interface Props {
  data: AOTDServerData;
}

type Tone = "ok" | "warn" | "err" | "info" | "muted";

interface TimelineNode {
  key: string;
  at?: string | Date | null;
  title: string;
  tone: Tone;
  sub?: string;
  body?: React.ReactNode;
}

const Wrap = styled.div`
  position: relative;
  padding-left: 26px;
`;

const Rail = styled.div`
  position: absolute;
  left: 9px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: ${colors.border};
`;

const Node = styled.div`
  position: relative;
  padding: 10px 0 14px;
`;

const Dot = styled.div<{ $tone: Tone }>`
  position: absolute;
  left: -22px;
  top: 14px;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: ${(p) =>
    p.$tone === "ok"
      ? colors.ok
      : p.$tone === "err"
      ? colors.err
      : p.$tone === "warn"
      ? colors.warn
      : p.$tone === "info"
      ? colors.info
      : colors.borderStrong};
  box-shadow: 0 0 0 3px ${colors.bg};
`;

const NodeHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const TimeLabel = styled.span`
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.78rem;
  color: ${colors.textMuted};
`;

const Title = styled.span`
  font-weight: 600;
  color: ${colors.text};
  font-size: 0.92rem;
`;

const Sub = styled.div`
  margin-top: 2px;
  color: ${colors.textMuted};
  font-size: 0.82rem;
`;

const Body = styled.div`
  margin-top: 8px;
`;

function ts(d?: string | Date | null): string {
  if (!d) return "—";
  try {
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return String(d);
    return date.toISOString().slice(11, 23) + "Z";
  } catch {
    return "—";
  }
}

function fullTs(d?: string | Date | null): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return "—";
  }
}

function delta(a?: string | Date | null, b?: string | Date | null): string | null {
  if (!a || !b) return null;
  const av = new Date(a).getTime();
  const bv = new Date(b).getTime();
  if (Number.isNaN(av) || Number.isNaN(bv)) return null;
  const ms = bv - av;
  const sign = ms < 0 ? "-" : "+";
  const abs = Math.abs(ms);
  if (abs < 1000) return `${sign}${abs}ms`;
  if (abs < 60_000) return `${sign}${(abs / 1000).toFixed(2)}s`;
  const m = Math.floor(abs / 60_000);
  const s = Math.round((abs % 60_000) / 1000);
  return `${sign}${m}m ${s}s`;
}

function fmtDuration(ms?: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

// Offset of an absolute epoch-ms time from the day's unlock instant — the only
// human-readable way to read a race (the absolute ISO times differ by ms).
function fmtOffset(absMs?: number | null, unlockMs?: number | null): string {
  if (absMs == null || unlockMs == null) return "—";
  const d = absMs - unlockMs;
  const sign = d < 0 ? "-" : "+";
  const abs = Math.abs(d);
  return abs < 1000 ? `${sign}${abs}ms` : `${sign}${(abs / 1000).toFixed(3)}s`;
}

function verdictTone(v?: string | null): Tone {
  if (v === "latency_corrected") return "ok";
  if (v === "arrival_fallback") return "warn";
  return "muted";
}

function verdictLabel(v?: string | null): string {
  if (v === "latency_corrected") return "corrected";
  if (v === "arrival_fallback") return "raw";
  return v || "—";
}

// Plain-language WHY a row fell back, so the panel surfaces the actual cause.
function fallbackLabel(reason?: string | null, wsAuthed?: boolean | null): string {
  if (reason === "no_ws_bucket") return wsAuthed ? "auth ws, no pongs" : "no ws / anonymous";
  if (reason === "insufficient_samples") return "starved (<3 pings)";
  if (reason === "latency_capped") return "capped (>2s)";
  return reason || "";
}

// Ranking key = aotdrace-2.0 `contestMs` (post-effects), falling back through
// the frozen legacy duplicate and raw arrival for pre-2.0 rows. CONTRACT.md §5.
function rankKey(p: RaceParticipantEntryData): number {
  if (typeof p.contestMs === "number") return p.contestMs;
  if (typeof p.effectiveSentAtMs === "number") return p.effectiveSentAtMs;
  if (p.serverArrivalAt) {
    const t = new Date(p.serverArrivalAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return Number.POSITIVE_INFINITY;
}

const RaceTable: FC<{ participants: RaceParticipantEntryData[]; unlockMs: number | null }> = ({
  participants,
  unlockMs,
}) => {
  const sorted = [...participants].sort((a, b) => rankKey(a) - rankKey(b));
  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th>#</th>
            <th>User</th>
            <th>Source</th>
            <th>Outcome</th>
            <th>Verdict</th>
            <th>Arrival</th>
            <th>Effective</th>
            <th>Contest</th>
            <th>1-way lat</th>
            <th>Pings</th>
            <th>Card</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p, i) => {
            const arrivalMs = p.serverArrivalAt ? new Date(p.serverArrivalAt).getTime() : null;
            const effMs = p.effectiveMs ?? p.rawEffectiveSentAtMs ?? p.effectiveSentAtMs ?? null;
            const conMs = p.contestMs ?? p.effectiveSentAtMs ?? null;
            const card =
              Array.isArray(p.appliedEffects) && p.appliedEffects.length
                ? p.appliedEffects
                    .map((e) => `${e.cardSlug || "?"} ${e.deltaMs ?? 0}ms`)
                    .join(", ")
                : p.appliedAdvantageMs
                ? `${p.appliedAdvantageMs}ms · ${p.appliedAdvantageCardSlug || "?"}`
                : "—";
            return (
              <tr key={(p.userId || "") + i}>
                <td>{i + 1}</td>
                <td>{p.username || p.userId || "—"}</td>
                <td>{p.source || "—"}</td>
                <td>
                  <Pill
                    $tone={
                      p.outcome === "winner"
                        ? "ok"
                        : p.outcome === "loser"
                        ? "muted"
                        : "warn"
                    }
                  >
                    {p.outcome || "—"}
                    {p.abortReason ? ` · ${p.abortReason}` : ""}
                  </Pill>
                </td>
                <td>
                  {p.clampVerdict ? (
                    <>
                      <Pill $tone={verdictTone(p.clampVerdict)}>
                        {verdictLabel(p.clampVerdict)}
                      </Pill>
                      {p.fallbackReason ? (
                        <div
                          style={{ fontSize: "0.72rem", color: colors.textMuted, marginTop: 2 }}
                        >
                          {fallbackLabel(p.fallbackReason, p.wsAuthenticatedEver)}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{fmtOffset(arrivalMs, unlockMs)}</td>
                <td>{fmtOffset(effMs, unlockMs)}</td>
                <td>{fmtOffset(conMs, unlockMs)}</td>
                <td>
                  {p.oneWayLatencyMs != null
                    ? `-${p.oneWayLatencyMs}ms`
                    : p.latencyEstimated === false
                    ? "none"
                    : "—"}
                </td>
                <td>
                  {p.latencySampleCountAtTap ??
                    (Array.isArray(p.pingSamples) ? p.pingSamples.length : "—")}
                </td>
                <td>{card}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
};

const LineageTable: FC<{ lineage: AotdStolenLineageEntryData[] }> = ({ lineage }) => (
  <TableScroll>
    <Table>
      <thead>
        <tr>
          <th>#</th>
          <th>When</th>
          <th>Previous author</th>
          <th>Stealer</th>
          <th>Outcome</th>
        </tr>
      </thead>
      <tbody>
        {lineage.map((l, i) => (
          <tr key={(l.previousAotdId || "") + i}>
            <td>{i + 1}</td>
            <td>{fullTs(l.stolenAt)}</td>
            <td>{l.previousAuthorUsername || "—"}</td>
            <td>{l.stealerUsername || "—"}</td>
            <td>
              <Pill $tone={l.outcome === "succeeded" ? "ok" : "muted"}>
                {l.outcome || "—"}
              </Pill>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </TableScroll>
);

// Pre-unlock polls: /today hits that refreshed before T₀ (raw pre-unlock) or
// whose latency-corrected tap landed before T₀ (aotdrace-2.0 corrected-pre-unlock
// DQ, CONTRACT §7.8.1). Logged to `earlyAttempts[]`, never entered into the race.
const EarlyPollTable: FC<{ attempts: EarlyAttemptData[]; unlockMs: number | null }> = ({
  attempts,
  unlockMs,
}) => {
  const sorted = [...attempts].sort(
    (a, b) =>
      new Date(a.serverArrivalAt ?? 0).getTime() -
      new Date(b.serverArrivalAt ?? 0).getTime(),
  );
  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th>#</th>
            <th>User</th>
            <th>Client sent</th>
            <th>Arrival</th>
            <th>Early by</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((a, i) => {
            const clientMs = a.clientSentAt ? new Date(a.clientSentAt).getTime() : null;
            const arrivalMs = a.serverArrivalAt ? new Date(a.serverArrivalAt).getTime() : null;
            return (
              <tr key={(a.userId || "") + i}>
                <td>{i + 1}</td>
                <td>{a.username || a.userId || "—"}</td>
                <td>{fmtOffset(clientMs, unlockMs)}</td>
                <td>{fmtOffset(arrivalMs, unlockMs)}</td>
                <td>
                  <Pill $tone="warn">
                    {typeof a.msEarly === "number" ? `-${Math.round(a.msEarly)}ms` : "—"}
                  </Pill>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
};

// Blocked polls: /today hits vetoed by an opponent's active card (Gridlock,
// etc.). Logged to `blockedAttempts[]`, never entered into the race.
const BlockedPollTable: FC<{ attempts: BlockedAttemptData[]; unlockMs: number | null }> = ({
  attempts,
  unlockMs,
}) => {
  const sorted = [...attempts].sort(
    (a, b) =>
      new Date(a.serverArrivalAt ?? 0).getTime() -
      new Date(b.serverArrivalAt ?? 0).getTime(),
  );
  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th>#</th>
            <th>User</th>
            <th>Arrival</th>
            <th>Blocked by</th>
            <th>Blockers</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((a, i) => {
            const arrivalMs = a.serverArrivalAt ? new Date(a.serverArrivalAt).getTime() : null;
            return (
              <tr key={(a.userId || "") + i}>
                <td>{i + 1}</td>
                <td>{a.username || a.userId || "—"}</td>
                <td>{fmtOffset(arrivalMs, unlockMs)}</td>
                <td>{a.blockedByCardSlug || "—"}</td>
                <td>{a.blockedByUsernames?.join(", ") || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
};

const StepBody: FC<{ step: PipelineStep; index: number }> = ({ step, index }) => (
  <Disclosure>
    <summary>
      <span style={{ color: colors.textDim, marginRight: 6 }}>{index + 1}.</span>
      <span style={{ flex: 1 }}>{step.name}</span>
      <Mono>{step.model}</Mono>
      <span style={{ color: colors.textMuted, marginLeft: 8 }}>
        {fmtDuration(step.durationMs)}
      </span>
    </summary>
    <div>
      {step.promptSent && (
        <>
          <SectionTitle>Prompt sent</SectionTitle>
          <CodeBlock>{step.promptSent}</CodeBlock>
        </>
      )}
      {step.rawResponse && (
        <div style={{ marginTop: 10 }}>
          <SectionTitle>Raw response</SectionTitle>
          <CodeBlock>{step.rawResponse}</CodeBlock>
        </div>
      )}
    </div>
  </Disclosure>
);

interface WaybackBodyProps {
  wb: WaybackMetadataData;
  onRetry?: () => void;
  retrying?: boolean;
  retryDone?: boolean;
}

const WaybackBody: FC<WaybackBodyProps> = ({ wb, onRetry, retrying, retryDone }) => (
  <Card>
    <Row>
      <Pill
        $tone={
          wb.status === "completed"
            ? wb.verified
              ? "ok"
              : "info"
            : wb.status === "failed"
            ? "err"
            : "muted"
        }
      >
        {wb.status || "—"}
        {wb.verified ? " · verified" : ""}
      </Pill>
      {wb.url && (
        <a
          href={wb.url}
          target="_blank"
          rel="noreferrer"
          style={{ color: colors.accent, fontSize: "0.85rem" }}
        >
          open snapshot →
        </a>
      )}
      {onRetry && wb.status !== "completed" && (
        <button
          onClick={onRetry}
          disabled={retrying || retryDone}
          style={{
            marginLeft: "auto",
            padding: "3px 10px",
            fontSize: "0.78rem",
            fontWeight: 600,
            border: `1px solid ${colors.border}`,
            borderRadius: 5,
            background: retryDone ? "#d4edda" : "#fff",
            color: retryDone ? "#155724" : colors.text,
            cursor: retrying || retryDone ? "default" : "pointer",
          }}
        >
          {retrying ? "queuing…" : retryDone ? "queued ✓" : "retry archive"}
        </button>
      )}
    </Row>
    {wb.error && <ErrorText>{wb.error}</ErrorText>}
    {retryDone && (
      <div style={{ marginTop: 6, fontSize: "0.8rem", color: "#155724" }}>
        Archive queued — check back in ~10 minutes.
      </div>
    )}
    {wb.screenshotUrl && (
      <div style={{ marginTop: 10 }}>
        <SectionTitle>Screenshot {wb.screenshotVerified ? "✓" : ""}</SectionTitle>
        <img
          src={wb.screenshotUrl}
          alt="wayback screenshot"
          style={{
            maxWidth: "100%",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
          }}
        />
      </div>
    )}
    <div style={{ marginTop: 10, fontSize: "0.8rem", color: colors.textMuted }}>
      requested: {fullTs(wb.requestedAt)} · captured: {fullTs(wb.capturedAt)}
      {wb.lastAttemptAt ? ` · last attempt: ${fullTs(wb.lastAttemptAt)}` : ""}
    </div>
  </Card>
);

export const Timeline: FC<Props> = ({ data }) => {
  const gs = data.generationStatus;
  const wb = data.metadata?.wayback;
  const tweet = data.tweet;
  const pl = data.pipelineMetadata;
  const ds = data.dayState;

  const [retryWayback, { isLoading: retrying }] = useRetryWaybackAdminMutation();
  const [retryDone, setRetryDone] = useState(false);

  const handleWaybackRetry = async () => {
    try {
      await retryWayback(data._id).unwrap();
      setRetryDone(true);
    } catch {
      // error is visible in the button state; no toast needed here
    }
  };

  const nodes: TimelineNode[] = [];

  if (ds?.scheduledTime) {
    nodes.push({
      key: "scheduled",
      at: ds.scheduledTime,
      title: "Day scheduled",
      tone: "muted",
      sub: ds.dayKey ? `dayKey ${ds.dayKey}` : undefined,
    });
  }
  if (ds?.openedAt) {
    nodes.push({
      key: "opened",
      at: ds.openedAt,
      title: "Day opened",
      tone: "info",
      sub: `Δ scheduled → opened: ${delta(ds.scheduledTime, ds.openedAt) ?? "—"}`,
    });
  }
  if (ds?.participants && ds.participants.length > 0) {
    const winner = ds.participants.find((p) => p.outcome === "winner");
    const unlockMs = ds.scheduledTime ? new Date(ds.scheduledTime).getTime() : null;
    // Latency coverage: how many real entrants actually got latency-corrected
    // (vs raw-arrival fallback). A low ratio means the race ranked corrected
    // and raw times against each other — the fairness risk. CONTRACT.md §5.
    const verdictRows = ds.participants.filter(
      (p) => p.source !== "simulation" && p.clampVerdict != null,
    );
    const correctedCount = verdictRows.filter(
      (p) => p.clampVerdict === "latency_corrected",
    ).length;
    const coverage =
      verdictRows.length > 0
        ? ` · latency ${correctedCount}/${verdictRows.length} corrected`
        : "";
    nodes.push({
      key: "race",
      at: ds.participants[0].serverArrivalAt,
      title: `Race · ${ds.participants.length} participant${ds.participants.length === 1 ? "" : "s"}`,
      tone: winner ? "ok" : "info",
      sub: `${winner ? `winner: ${winner.username}` : "no winner yet"}${coverage}`,
      body: <RaceTable participants={ds.participants} unlockMs={unlockMs} />,
    });
  }
  if (ds?.earlyAttempts && ds.earlyAttempts.length > 0) {
    const unlockMs = ds.scheduledTime ? new Date(ds.scheduledTime).getTime() : null;
    nodes.push({
      key: "early-polls",
      at: ds.earlyAttempts[0].serverArrivalAt,
      title: `Pre-unlock polls · ${ds.earlyAttempts.length}`,
      tone: "warn",
      sub: "refreshed before unlock (or latency-corrected before T₀) — not entered into the race",
      body: <EarlyPollTable attempts={ds.earlyAttempts} unlockMs={unlockMs} />,
    });
  }
  if (ds?.blockedAttempts && ds.blockedAttempts.length > 0) {
    const unlockMs = ds.scheduledTime ? new Date(ds.scheduledTime).getTime() : null;
    nodes.push({
      key: "blocked-polls",
      at: ds.blockedAttempts[0].serverArrivalAt,
      title: `Blocked polls · ${ds.blockedAttempts.length}`,
      tone: "warn",
      sub: "vetoed by an opponent's active card — not entered into the race",
      body: <BlockedPollTable attempts={ds.blockedAttempts} unlockMs={unlockMs} />,
    });
  }
  if (ds?.claimedAt) {
    nodes.push({
      key: "claimed",
      at: ds.claimedAt,
      title: `Claimed by ${ds.claimedByUsername || "?"}`,
      tone: "ok",
      sub: `Δ opened → claimed: ${delta(ds.openedAt, ds.claimedAt) ?? "—"}`,
    });
  }
  if (ds?.lastClaimBlockedAt) {
    nodes.push({
      key: "claim-blocked",
      at: ds.lastClaimBlockedAt,
      title: "Claim blocked",
      tone: "warn",
      sub: ds.lastClaimBlockedReason || undefined,
    });
  }
  if (gs?.startedAt) {
    nodes.push({
      key: "gen-start",
      at: gs.startedAt,
      title: "Generation started",
      tone: "info",
      sub:
        ds?.claimedAt
          ? `Δ claimed → started: ${delta(ds.claimedAt, gs.startedAt) ?? "—"}`
          : undefined,
    });
  }
  if (pl?.steps && pl.steps.length > 0) {
    nodes.push({
      key: "pipeline",
      at: gs?.startedAt,
      title: `Pipeline · ${pl.steps.length} step${pl.steps.length === 1 ? "" : "s"}`,
      tone: "info",
      sub: pl.totalDurationMs
        ? `total ${fmtDuration(pl.totalDurationMs)}${
            pl.steps[0]?.model ? ` · ${pl.steps[0].model}` : ""
          }`
        : undefined,
      body: (
        <div>
          {pl.concept && (
            <>
              <SectionTitle>Concept</SectionTitle>
              <Card style={{ marginBottom: 8 }}>{pl.concept}</Card>
            </>
          )}
          {pl.artStyle && (
            <>
              <SectionTitle>Art style</SectionTitle>
              <Card style={{ marginBottom: 8 }}>{pl.artStyle}</Card>
            </>
          )}
          {pl.critique && (
            <>
              <SectionTitle>Critique</SectionTitle>
              <Card style={{ marginBottom: 8 }}>{pl.critique}</Card>
            </>
          )}
          {pl.imagePrompt && (
            <>
              <SectionTitle>Image prompt</SectionTitle>
              <Card style={{ marginBottom: 8 }}>{pl.imagePrompt}</Card>
            </>
          )}
          <SectionTitle>Steps</SectionTitle>
          {pl.steps.map((step, i) => (
            <StepBody key={i} step={step} index={i} />
          ))}
        </div>
      ),
    });
  }
  if (gs?.error || gs?.status === "error" || gs?.status === "failed") {
    nodes.push({
      key: "gen-error",
      at: gs?.completedAt || gs?.startedAt,
      title: "Generation error",
      tone: "err",
      sub: gs?.message || gs?.error || "unknown error",
      body: gs?.error ? <CodeBlock>{gs.error}</CodeBlock> : undefined,
    });
  }
  if (gs?.completedAt) {
    nodes.push({
      key: "gen-done",
      at: gs.completedAt,
      title: "Generation completed",
      tone: "ok",
      sub: `Δ started → completed: ${delta(gs.startedAt, gs.completedAt) ?? "—"}${
        gs.state ? ` · state ${gs.state}` : ""
      }`,
    });
  }
  if (tweet?.datePosted || tweet?.tweetUrl) {
    nodes.push({
      key: "tweet",
      at: tweet.datePosted,
      title: "Tweet posted",
      tone: "ok",
      sub: tweet.tweetUrl,
      body: tweet.tweetUrl ? (
        <Card>
          <a
            href={tweet.tweetUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: colors.accent }}
          >
            {tweet.tweetUrl}
          </a>
          {tweet.tweetId && (
            <div style={{ color: colors.textMuted, fontSize: "0.82rem", marginTop: 4 }}>
              tweet ID: <Mono>{tweet.tweetId}</Mono>
            </div>
          )}
        </Card>
      ) : undefined,
    });
  }
  if (gs?.state === "posted_tg" || gs?.state === "done") {
    nodes.push({
      key: "tg",
      title: "Telegram broadcast",
      tone: "ok",
      sub: "(no exact timestamp recorded — state ≥ posted_tg)",
    });
  }
  if (wb && (wb.requestedAt || wb.status)) {
    nodes.push({
      key: "wayback",
      at: wb.requestedAt || wb.capturedAt || wb.lastAttemptAt,
      title: "Wayback capture",
      tone:
        wb.status === "completed"
          ? wb.verified
            ? "ok"
            : "info"
          : wb.status === "failed"
          ? "err"
          : "warn",
      sub: wb.url || wb.status || undefined,
      body: (
        <WaybackBody
          wb={wb}
          onRetry={handleWaybackRetry}
          retrying={retrying}
          retryDone={retryDone}
        />
      ),
    });
  } else {
    nodes.push({
      key: "wayback",
      title: "Wayback capture",
      tone: "muted",
      sub: "no archive attempt recorded",
      body: (
        <Card>
          <Row>
            <Pill $tone="muted">not attempted</Pill>
            <button
              onClick={handleWaybackRetry}
              disabled={retrying || retryDone}
              style={{
                marginLeft: "auto",
                padding: "3px 10px",
                fontSize: "0.78rem",
                fontWeight: 600,
                border: `1px solid ${colors.border}`,
                borderRadius: 5,
                background: retryDone ? "#d4edda" : "#fff",
                color: retryDone ? "#155724" : colors.text,
                cursor: retrying || retryDone ? "default" : "pointer",
              }}
            >
              {retrying ? "queuing…" : retryDone ? "queued ✓" : "trigger archive"}
            </button>
          </Row>
          {retryDone && (
            <div style={{ marginTop: 6, fontSize: "0.8rem", color: "#155724" }}>
              Archive queued — check back in ~10 minutes.
            </div>
          )}
        </Card>
      ),
    });
  }
  if (ds?.stolenLineage && ds.stolenLineage.length > 0) {
    nodes.push({
      key: "lineage",
      at: ds.stolenLineage[ds.stolenLineage.length - 1]?.stolenAt,
      title: `Stolen lineage · ${ds.stolenLineage.length}`,
      tone: "warn",
      body: <LineageTable lineage={ds.stolenLineage} />,
    });
  }

  if (nodes.length === 0) {
    return <Hint>No timeline events recorded yet.</Hint>;
  }

  return (
    <Wrap>
      <Rail />
      {nodes.map((n) => (
        <Node key={n.key}>
          <Dot $tone={n.tone} />
          <NodeHead>
            <TimeLabel>{ts(n.at)}</TimeLabel>
            <Title>{n.title}</Title>
          </NodeHead>
          {n.sub && <Sub>{n.sub}</Sub>}
          {n.body && <Body>{n.body}</Body>}
        </Node>
      ))}
    </Wrap>
  );
};
